import React, { useEffect, useMemo, useRef, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import {
  enBiomarkerCategory,
  getProviderLabel,
  getPanelTestKindsLabel,
  formatBiomarkerResultValue,
  getBiomarkerCategoryColor,
} from "../../store/helper";
import {
  getPanelOrigin,
  normalizeLabPanelResults,
  BiomarkerResult,
} from "../../utils/labResults";
import { formatDisplayDate } from "../../../../common/utils/date";
import CustomTooltip from "./CustomTooltip";
import LabSvg from "../../../../common/svgs/LabSvg";
import UnsavedBiomarkersNotice from "../biomarkerTab/UnsavedBiomarkersNotice";
import {
  getPanelQuestions,
  getQuestionAnswerLabel,
  getQuestionBiomarkerNames,
  getQuestionText,
  hasAnsweredQuestions,
} from "../../utils/labPanelQuestions";
import InfoSvg from "../../../../common/svgs/InfoSvg";

const SMALL_CONTAINER_WIDTH = 500;

interface PanelSummaryCardProps {
  panel: any;
  showHeader?: boolean;
  // Optional content rendered between the info row and the biomarkers section
  // (e.g. the order status banner in the order detail view).
  beforeBiomarkers?: React.ReactNode;
  // Once results are in (approval/completed statuses), render each biomarker as
  // a result row (name | value | reference range) instead of the name list.
  showResults?: boolean;
  // The order-entry questions box. Off where the questions are being answered
  // in inputs right above the card (the prescription builder), so they are
  // not listed twice.
  showQuestions?: boolean;
  // A client's lab order (not a panel definition): adds Panel origin, and the
  // Sample date once the lab has one, at every status.
  showOrderInfo?: boolean;
}

const PanelSummaryCard: React.FC<PanelSummaryCardProps> = ({
  panel,
  showHeader = false,
  beforeBiomarkers,
  showResults = false,
  showQuestions = true,
  showOrderInfo = false,
}) => {
  const theme: Theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsSmall(entry.contentRect.width < SMALL_CONTAINER_WIDTH);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const totalCost = useMemo(() => {
    // Cost is carried at the panel level, under `price` on the provider/custom
    // catalog objects and under `total` on the saved lab-panel entity (loaded
    // inside a logic/order). Fall back to summing item prices only when neither
    // is set — some panels (e.g. urine albumin) keep their cost solely at the
    // panel level with $0 items, which would otherwise show as $0.
    if (typeof panel.price === "number" && panel.price > 0) return panel.price;
    if (typeof panel.total === "number" && panel.total > 0) return panel.total;
    return (
      panel.items?.reduce(
        (sum: number, item: any) => sum + (item.price || 0),
        0
      ) || 0
    );
  }, [panel.items, panel.price, panel.total]);

  const groupedByCategory = useMemo(() => {
    if (!panel.items || panel.items.length === 0) return [];

    const groups: Record<string, any[]> = {};
    panel.items.forEach((item: any) => {
      const key = String(item.category ?? "0");
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    return Object.entries(groups).sort(([a], [b]) => {
      if (a === "0") return 1;
      if (b === "0") return -1;
      return a.localeCompare(b);
    });
  }, [panel.items]);

  const biomarkerCount = panel.items?.length || 0;

  // Order-entry questions, once each in the lab's order; `answered` once the
  // panel was ordered (then every question has an answer).
  const questions = useMemo(() => getPanelQuestions(panel), [panel]);
  const answered = hasAnsweredQuestions(questions);

  // Results mode: normalize the panel into the slim results shape, then group
  // the (normalized) biomarker results by category for the row layout.
  const results = useMemo(
    () => (showResults ? normalizeLabPanelResults(panel) : null),
    [showResults, panel]
  );

  // Biomarkers the lab couldn't process — surfaced in a notice, not the table.
  const failedItems = useMemo(
    () => results?.items.filter((i) => i.failed) ?? [],
    [results]
  );

  const resultsByCategory = useMemo(() => {
    if (!results) return [];
    const groups: Record<string, BiomarkerResult[]> = {};
    results.items
      .filter((item) => !item.failed)
      .forEach((item) => {
        const key = String(item.category ?? "0");
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === "0") return 1;
      if (b === "0") return -1;
      return a.localeCompare(b);
    });
  }, [results]);

  // Biomarkers the lab returned that weren't in the original order (failed
  // items are excluded — they're surfaced in the notice, not counted).
  const addedCount = useMemo(
    () => results?.items.filter((i) => i.added && !i.failed).length ?? 0,
    [results]
  );

  // Count shown in the title excludes failed items (not processed by the lab).
  const shownCount = biomarkerCount - failedItems.length;

  const s = styles;

  return (
    <div ref={containerRef} style={s.wrapper}>
      {showHeader && <div style={styles.panelName(theme)}>{panel.name}</div>}

      {/* Info row: Provider, Test type, Total cost (+ Panel origin, Sample date on orders) */}
      <div style={s.infoRow(isSmall)}>
        <div style={s.infoItem}>
          <div style={s.infoLabel(theme)}>Provider</div>
          <div style={s.infoValue(theme)}>
            {getProviderLabel(panel.provider)}
          </div>
        </div>
        <div style={s.infoItem}>
          <div style={s.infoLabel(theme)}>Test type</div>
          <div style={s.infoValue(theme)}>{getPanelTestKindsLabel(panel)}</div>
        </div>
        <div style={s.infoItem}>
          <div style={s.infoLabel(theme)}>Total cost</div>
          <div style={s.infoValue(theme)}>${totalCost.toFixed(2)}</div>
        </div>
        {showOrderInfo && (
          <div style={s.infoItem}>
            <div style={s.infoLabel(theme)}>Panel origin</div>
            <div style={s.infoValue(theme)}>{getPanelOrigin(panel)}</div>
          </div>
        )}
        {showOrderInfo && panel.sampleDate && (
          <div style={s.infoItem}>
            <div style={s.infoLabel(theme)}>Sample date</div>
            <div style={s.infoValue(theme)}>
              {formatDisplayDate(panel.sampleDate)}
            </div>
          </div>
        )}
      </div>

      {beforeBiomarkers}

      {/* Biomarkers section */}
      <div style={s.biomarkersSection}>
        <div style={s.biomarkersTitle(theme)}>
          {`Biomarkers (${
            addedCount > 0
              ? `${shownCount - addedCount} + ${addedCount} Added by lab`
              : shownCount
          })`}
        </div>
        {failedItems.length > 0 && (
          <UnsavedBiomarkersNotice
            title={"Not processed by the lab"}
            message={`The lab couldn’t process ${failedItems.length} biomarker${
              failedItems.length === 1 ? "" : "s"
            }, so ${
              failedItems.length === 1 ? "it isn’t" : "they aren’t"
            } shown below.`}
            items={failedItems.map((item) => ({
              name: item.name,
              value: "",
              unit: "",
              reason: item.reason || "No reason provided",
            }))}
          />
        )}
        {results ? (
          // Results in: one row per biomarker (name | value | reference range),
          // grouped by category. Value colored by its score category.
          <div style={s.resultsGroups}>
            {resultsByCategory.map(([category, items]) => (
              <div key={category} style={s.resultsGroup}>
                <div style={s.categoryLabel(theme)}>
                  {enBiomarkerCategory[Number(category)] ?? category}
                </div>
                <div style={s.resultsTable(theme)}>
                  {items.map((item, i) => (
                    <div
                      key={item.id}
                      style={{
                        ...s.resultRow(theme),
                        ...(item.added ? s.resultRowAdded(theme) : {}),
                        ...(i === items.length - 1
                          ? { borderBottom: "none" }
                          : {}),
                      }}
                    >
                      <div style={s.resultName(theme)}>
                        <div>{item.name}</div>
                        {item.added && (
                          <CustomTooltip
                            customHeight={"22px"}
                            type={"medium"}
                            message={
                              "The lab included this biomarker with your results even though it wasn't in the original order."
                            }
                          >
                            <div style={s.addedTag(theme)}>
                              <LabSvg
                                fill={theme.colors_ds.NEUTRAL_TEXT_WEAK}
                                width={11}
                                height={12}
                              />
                              Added by lab
                            </div>
                          </CustomTooltip>
                        )}
                      </div>
                      <span
                        style={{
                          ...s.resultValue,
                          color: getBiomarkerCategoryColor(
                            item.scoreCategory,
                            theme,
                            "biomarkers"
                          ),
                        }}
                      >
                        {formatBiomarkerResultValue(item)}
                      </span>
                      <span style={s.resultRange(theme)}>{item.label}</span>
                      <span style={s.resultDate(theme)}>
                        {item.dateUpdated
                          ? formatDisplayDate(item.dateUpdated)
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={s.categoryGrid(isSmall)}>
            {groupedByCategory.map(([category, items]) => (
              <div key={category} style={s.categoryItem}>
                <div style={s.categoryLabel(theme)}>
                  {enBiomarkerCategory[Number(category)] ?? category}
                </div>
                <div style={s.categoryValues(theme)}>
                  {items.map((b: any) => b.name || b.biomarkerid).join(", ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Questions the lab asks at order entry. Shown for the panel as a
          whole: the lab keys them by the panel's test code, not by biomarker.
          Highlighted, because they must be answered when the panel is
          prescribed. Before the order only the question (and its
          accepted-answer format) is shown; once ordered every question is
          required, so each carries the answer that was given. A question
          that belongs to some of the panel's biomarkers (a custom panel
          mixing tests) names them behind an info icon. */}
      {showQuestions && questions.length > 0 && (
        <div style={s.questionsBox(theme, answered)}>
          <div style={s.questionsNotice(theme)}>
            {answered
              ? "Answers given to the lab's order-entry questions"
              : `This panel has ${questions.length} question${
                  questions.length === 1 ? "" : "s"
                } the lab requires at order entry. They must be answered when the panel is prescribed.`}
          </div>
          <div style={s.categoryGrid(isSmall)}>
            {questions.map((question) => {
              const answer = getQuestionAnswerLabel(question);
              const biomarkers = getQuestionBiomarkerNames(panel, question);
              const forBiomarkers = biomarkers.length > 0 && (
                <CustomTooltip
                  inline
                  type="medium"
                  customHeight="auto"
                  message={`Asked for: ${biomarkers.join(", ")}`}
                >
                  <span
                    style={s.questionInfoIcon}
                    aria-label={`Asked for ${biomarkers.join(", ")}`}
                  >
                    <InfoSvg
                      color={theme.colors_ds.NEUTRAL_ICON_WEAK}
                      size={12}
                    />
                  </span>
                </CustomTooltip>
              );
              return (
                <div key={question.id ?? question.code} style={s.categoryItem}>
                  {answered ? (
                    <>
                      <div style={s.categoryLabel(theme)}>
                        {getQuestionText(question)}
                        {forBiomarkers}
                      </div>
                      <div style={s.categoryValues(theme)}>{answer ?? "—"}</div>
                    </>
                  ) : (
                    <>
                      <div style={s.questionText(theme)}>
                        {getQuestionText(question)}
                        {forBiomarkers}
                      </div>
                      {question.constraint && (
                        <div style={s.questionConstraint(theme)}>
                          {question.constraint}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelSummaryCard;

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  panelName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  infoRow: (isSmall: boolean) => ({
    display: "grid",
    gridTemplateColumns: isSmall ? "1fr" : "repeat(3, minmax(0, 300px))",
    gap: isSmall ? "12px" : "16px 52px",
  }),
  infoItem: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  infoLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  infoValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  biomarkersSection: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  biomarkersTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  categoryGrid: (isSmall: boolean) => ({
    display: "grid",
    gridTemplateColumns: isSmall ? "1fr" : "repeat(3, minmax(0, 300px))",
    gap: isSmall ? "12px" : "16px 52px",
  }),
  categoryItem: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  categoryLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  categoryValues: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  // The questions sit in a neutral grey box so they are not missed: they
  // have to be answered at prescription time.
  // Warning tint while the questions still have to be answered (panel and
  // logic details); neutral once the order carries the answers (prescription
  // and order details), where the box is a record rather than a to-do.
  questionsBox: (theme: Theme, answered: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    padding: "12px 16px",
    borderRadius: "8px",
    backgroundColor: answered
      ? theme.colorTokens.N[20]
      : theme.colorTokens.W[10],
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  questionsNotice: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  // The "asked for these biomarkers" icon after a question's text. Text and
  // icon flow inline (the tooltip anchor is inline-flex, middle-aligned), so
  // the icon follows the last word of a question that wraps instead of
  // dropping to a line of its own as a flex item would.
  questionInfoIcon: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: "4px",
    cursor: "default",
  },
  // An unanswered question reads as content, not as a label: default colour,
  // with the accepted-answer format beneath in the weak colour. Once answered
  // the question becomes the label and the answer the value (the same
  // treatment as the info row above).
  questionText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  questionConstraint: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  // ── Results rows (approval/completed statuses) ──────────────────────────
  resultsGroups: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  resultsGroup: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  resultsTable: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden" as "hidden",
  }),
  resultRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  resultName: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  // Lab-added row: a warning-colored left accent bar (no background tint).
  resultRowAdded: (theme: Theme) => ({
    boxShadow: `inset 3px 0 0 0 ${theme.colors_ds.WARNING_GRAPH_MAIN}`,
  }),
  // Neutral "Added by lab" pill — kept gray so it doesn't clash with the
  // score-colored values. The tooltip on it explains why the row is here.
  addedTag: (theme: Theme) => ({
    // Block-level flex (not inline-flex): CustomTooltip wraps this in a plain
    // block div, and an inline-flex child would sit on that div's text baseline
    // (descender gap below), making the pill look too high. fit-content keeps
    // it shrink-to-content.
    display: "flex",
    width: "fit-content",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_DEFAULT}`,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "12px",
    padding: "4px 8px",
    borderRadius: "999px",
    whiteSpace: "nowrap" as "nowrap",
    cursor: "help",
  }),
  addedNote: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "4px",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  resultValue: {
    width: "180px",
    flexShrink: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  },
  resultRange: (theme: Theme) => ({
    width: "220px",
    flexShrink: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  resultDate: (theme: Theme) => ({
    width: "110px",
    flexShrink: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
